import * as React from "react";
import {InputGroup, OverlayTrigger, Tooltip} from "react-bootstrap";
import {LcdIcon} from "../icon/LcdIcon";
import {Spinner} from "../spinner/Spinner";
import {defineMessages, InjectedIntlProps, injectIntl} from "react-intl";

const MESSAGES = defineMessages({
  LONG_ERROR: {
    id: "studio.property.number-error.long",
    defaultMessage: "Invalid Long number"
  },
  DOUBLE_ERROR: {
    id: "studio.property.number-error.double",
    defaultMessage: "Invalid Double number"
  },
  NONE: {
    id: "studio.property.edit.none",
    defaultMessage: "None"
  }
});

interface NumberEditProps {
  name?: string;
  value: number;
  defaultValue?: number;
  type?: string;
  isLong?: boolean;
  onChange: (newValue: number) => void | Promise<void>;
  onBlur?: React.FocusEventHandler<any>;
  readOnly?: boolean;
}

interface NumberEditState {
  isMouseOver: boolean;
  isEditing: boolean;
  editingValue: number;
  submitState: "NOT_STARTED" | "SUBMITTING" | "ERROR"; //no explicit FINISHED state, the edit field just goes back to 'display' mode
  submitError: Error;
  lastMouseOut: number;
}

/**
 * A Double Number edit field that can be edited upon mouseover.
 */
class NumberEditComponent extends React.Component<NumberEditProps & InjectedIntlProps, NumberEditState> {

  _textInput: HTMLInputElement;

  constructor(props) {
    super(props);
    this.state = {
      isMouseOver: false,
      isEditing: false,
      editingValue: null,
      submitState: "NOT_STARTED",
      submitError: null,
      lastMouseOut: 0,
    };
  }

  reset = () => {
    this.setState({
      isEditing: false,
      editingValue: null,
      submitState: "NOT_STARTED",
      submitError: null,
    });
  }

  handleMouseOver = () => {
    // Remove this time hack when implementing a real solution for LF-2229
    if (Date.now() - this.state.lastMouseOut > 20) {
      this.setState({isMouseOver: true});
    }
  }

  handleMouseOut = () => {
    this.setState({isMouseOver: false, lastMouseOut: Date.now()});
  }

  validateNumber = (newValue: string): boolean => {
    const {isLong} = this.props;
    try {
      const newNumber = isLong ? parseInt(newValue, 10) : parseFloat(newValue);
      if (isLong && (newNumber > Number.MAX_SAFE_INTEGER || newNumber < Number.MIN_SAFE_INTEGER)) {
        return false;
      }
      if (!isLong && (newNumber > Number.MAX_VALUE || newNumber < -Number.MAX_VALUE)) {
        return false;
      }
      return !isNaN(newNumber)
    } catch (NumberFormatException) {
      return false;
    }
  }

  handleChange = () => {
    const {isLong, intl} = this.props;
    const newValue = this._textInput.value;
    const isValid = this.validateNumber(newValue);
    if (isValid || newValue === "") {
      //remove the error state when changing the input after an error
      this.setState(prevState => ({
        editingValue: isValid ? Number(newValue) : null,
        submitState: prevState.submitState === "ERROR" ? "NOT_STARTED" : prevState.submitState
      }));
    } else {
      this.setState({
        submitState: "ERROR",
        submitError: {
          name: "Invalid Number",
          message: isLong ? intl.formatMessage(MESSAGES.LONG_ERROR) : intl.formatMessage(MESSAGES.DOUBLE_ERROR)
        }
      });
    }
  }

  handleInputFocus = () => {
    if (this._textInput) {
      this._textInput.select();
    }
    this.setState(prevState => ({
      isEditing: true,
      editingValue: prevState.submitState === "ERROR" ? prevState.editingValue : this.props.value
    }));
  }

  handleInputBlur = (blurEvent) => {
    if (this.state.editingValue !== this.props.value && (this.state.submitState !== "ERROR")) {
      this.valueChanged(this.state.editingValue).then(this.reset).catch(this.reset);
    } else {
      this.reset();
    }
    if (this.props.onBlur) {
      this.props.onBlur(blurEvent);
    }
  }

  valueChanged = (newValue: number): Promise<void> => {
    const onChangeResult = this.props.onChange(newValue);
    if (onChangeResult instanceof Promise) {
      this.setState({submitState: "SUBMITTING"});
      const onChangeResultPromise = onChangeResult as Promise<void>;
      return onChangeResultPromise.catch((error) => {
        this.setState({submitState: "ERROR", submitError: error});
        this._textInput.focus();
        throw error;
      });
    } else {
      return Promise.resolve();
    }
  }

  handleKeyPress = (event: React.KeyboardEvent<any>) => {
    //submit on ENTER
    if (event.key === 'Enter' && this._textInput) {
      this._textInput.blur();
    }
  }

  handleKeyDown = (event: React.KeyboardEvent<any>) => {
    if (this.props.isLong && event.key === '.') {
      event.preventDefault();
    }
  }

  handlePaste = (event) => {
    if (this.props.isLong) {
      const regex = /^\d*$/;
      const copiedText = event?.clipboardData?.getData('text')
      if (copiedText && !regex.test(copiedText)) {
        event.preventDefault();
        return false;
      }
    }
  }

  shouldShowInput = () => {
    const {isMouseOver, isEditing} = this.state;
    return isMouseOver || isEditing;
  }

  escapeKeyPressedListener = (event: KeyboardEvent) => {
    //cancel on ESC, unfortunately ESC keys are only dispatched on document
    if (event.key === 'Escape' && this._textInput) {
      //revert editingValue to this.props.value -> no onChange fired
      this.setState({editingValue: this.props.value});
      this._textInput.blur();
    }
  }

  isNotANumber = (value: any): boolean => {
    return isNaN(this.props.isLong ? parseInt(value?.toString(), 10) : parseFloat(value?.toString()));
  }

  renderInput = () => {
    const {intl} = this.props;

    if (this.shouldShowInput() && !this.props.readOnly) {
      const inputValue = this.state.isEditing ? this.state.editingValue : this.props.value;
      document.addEventListener("keyup", this.escapeKeyPressedListener);
      //use a 'vanilla' input iso a FormControl to allow more low-level handling of events (select/focus/blur methods)
      return <input type={this.props.type || "number"}
                    className="form-control numberEditInput"
                    ref={(ref) => {
                      this._textInput = ref;
                    }}
                    onBlur={this.handleInputBlur}
                    onFocus={this.handleInputFocus}
                    onChange={this.handleChange}
                    onKeyPress={this.handleKeyPress}
                    onKeyDown={this.handleKeyDown}
                    onPaste={this.handlePaste}
                    name={this.props.name}
                    value={this.isNotANumber(inputValue) ? "" : inputValue}
      />;
    }
    this._textInput = null;
    document.removeEventListener("keyup", this.escapeKeyPressedListener);
    return (<div className="numberEditDisplay">
      {this.props.children || this.isNotANumber(this.props.value) ? this.props.defaultValue ||
                                                                    intl.formatMessage(MESSAGES.NONE)
                                                                  : this.props.value}
    </div>);
  }

  renderAddon = () => {
    if (this.props.readOnly) {
      return null;
    }
    const {submitState, submitError} = this.state;
    switch (submitState) {
    case "NOT_STARTED":
      return null;
    case "SUBMITTING":
      return <InputGroup.Addon><Spinner/></InputGroup.Addon>;
    case "ERROR": {
      const errorMessage = submitError.message;
      const tooltip = <Tooltip id={(this.props.name || "textedit") + "-error-tooltip"}>{errorMessage}</Tooltip>;
      return (
          <InputGroup.Addon>
            <OverlayTrigger placement="top" overlay={tooltip} defaultOverlayShown={true}>
              <LcdIcon icon="alert"/>
            </OverlayTrigger>
          </InputGroup.Addon>
      );
    }
    }
  }

  render() {
    return (
        <InputGroup className="numberEditContainer"
                    onMouseOver={this.handleMouseOver}
                    onMouseOut={this.handleMouseOut}>
          {this.renderInput()}{this.renderAddon()}
        </InputGroup>
    );
  }
}

export const NumberEdit = injectIntl(NumberEditComponent);